import { styled, GetProps, YStack, XStack, Text } from 'tamagui'
import type { ReactNode } from 'react'
import { Heading } from '../../atoms/Typography/Heading'

const SectionFrame = styled(YStack, {
  name: 'Section',
  tag: 'section',
  paddingVertical: '$6',
  borderTopWidth: 2,
  borderColor: '$borderColor',
  
  variants: {
    border: {
      visible: {
        borderTopWidth: 2,
        borderColor: '$borderColor',
      },
      none: {
        borderTopWidth: 0,
        borderColor: 'transparent',
      },
    },
  } as const,
  
  defaultVariants: {
    border: 'visible',
  },
})

const SectionHeader = styled(XStack, {
  name: 'SectionHeader',
  tag: 'header',
  gap: '$3',
  alignItems: 'flex-start',
  marginBottom: '$4',
})

const SectionNumber = styled(Text, {
  name: 'SectionNumber',
  fontFamily: '$mono',
  fontSize: 32,
  fontWeight: '700',
  color: '$colorMuted',
  lineHeight: 32,
})

const SectionMeta = styled(YStack, {
  name: 'SectionMeta',
  flex: 1,
})

const SectionLabel = styled(Text, {
  name: 'SectionLabel',
  fontSize: 10,
  fontWeight: '600',
  textTransform: 'uppercase',
  letterSpacing: 2,
  color: '$colorMuted',
  marginBottom: '$1',
})

const SectionContent = styled(YStack, {
  name: 'SectionContent',
})

export interface SectionProps extends GetProps<typeof SectionFrame> {
  number?: string
  label?: string
  title: string
  children: ReactNode
}

export function Section({
  number,
  label,
  title,
  children,
  ...props
}: SectionProps) {
  return (
    <SectionFrame {...props}>
      <SectionHeader>
        {number && <SectionNumber>{number}</SectionNumber>}
        <SectionMeta>
          {label && <SectionLabel>{label}</SectionLabel>}
          <Heading size="md" level="h2">{title}</Heading>
        </SectionMeta>
      </SectionHeader>
      <SectionContent>
        {children}
      </SectionContent>
    </SectionFrame>
  )
}

Section.displayName = 'Section'
